import { CdkDragPlaceholder } from '@angular/cdk/drag-drop';
import { CommonModule, DatePipe, formatDate } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
  ChangeDetectionStrategy,
  LOCALE_ID,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  injectShellEnvironment,
  isEmbeddedShellEnvironment,
  menuBar,
  panel,
  ShellService,
} from '@softapps/core';
import { PageComponent, Panel, PanelHeaderComponent } from '@softapps/core/pages';
import {
  BackNavigable,
  BackNavigationService,
  FilterAndSortComponent,
  FilterConfig,
  menuItems,
  Refreshable,
  RefreshService,
  showRequestErrors,
  SOFTLINE_FEATURE_TITLE,
  SortConfig,
  TitleStore,
} from '@softline/application';
import {
  explore,
  Filter,
  FilterService,
  isDefinedNotEmpty,
  LocalStorageDataAccess,
  Sort,
  SortService,
  Store,
  withFilter,
  withSearch,
  withSort,
} from '@softline/core';
import {
  BreakpointService,
  ButtonComponent,
  ButtonGroupComponent,
  EmptyStateComponent,
  IconComponent,
  SoftSlotDirective,
  translator,
} from '@softline/ui-core';
import { combineLatestWith, interval, map, Observable } from 'rxjs';
import { AbteilungCardComponent } from '../../components/abteilung-card/abteilung-card.component';
import { AbteilungenService } from '../../services/abteilungen.service';
import { DateNavigatorService } from '../../services/date-navigator.service';
import {
  SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN,
  SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER,
} from '../../statusabfrage.shared';
import { PmsAbteilungStore } from '../../store/abteilung.store';
import { PmsPersonalStore } from '../../store/personal.store';
import { AbteilungUebersicht } from '../../types/abteilung';
import { injectSearchQuery } from '../../utils/inject-search-query';

const AUTO_REFRESH_INTERVAL_MS = 3 * 60_000;

type PresenceStatus = AbteilungUebersicht['status'] | null | undefined;

const presenceTotal = (value: unknown): number => {
  const status = value as PresenceStatus;
  return (
    (status?.anwesend ?? 0) + (status?.abwesendgelb ?? 0) + (status?.abwesendrot ?? 0)
  );
};

const presenceRatio = (value: unknown): number => {
  const total = presenceTotal(value);
  return total ? ((value as PresenceStatus)?.anwesend ?? 0) / total : 0;
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'soft-abteilungen',
  templateUrl: './abteilungen.component.html',
  styleUrls: ['./abteilungen.component.css'],
  imports: [
    CommonModule,
    RouterLink,
    FilterAndSortComponent,
    AbteilungCardComponent,
    CdkDragPlaceholder,
    PageComponent,
    Panel,
    PanelHeaderComponent,
    EmptyStateComponent,
    ButtonComponent,
    ButtonGroupComponent,
    IconComponent,
    SoftSlotDirective,
  ],
  providers: [DatePipe],
  host: {
    '[style.maxHeight]': 'maxHeightCalc()',
  },
})
export class AbteilungenComponent
  implements OnInit, OnDestroy, BackNavigable, Refreshable
{
  readonly shellEnvironment = injectShellEnvironment();
  readonly shellService = inject(ShellService);

  readonly contentRect = toSignal(this.shellService.contentRect$, {
    initialValue: undefined,
  });

  readonly offsetTop = computed(() => {
    const isEmbeddedContext = isEmbeddedShellEnvironment(this.shellEnvironment);
    const offsetTop = this.contentRect()?.top ?? 0;
    if (isEmbeddedContext) return offsetTop;
    return offsetTop > 90 ? offsetTop : offsetTop + 92; // height of page header with date-navigator = 92px
  });

  readonly maxHeightCalc = computed(() => `calc(100vh - ${this.offsetTop()}px)`);

  private unregisterDateChange?: () => void;

  textFilterFromQueryParam = injectSearchQuery();

  readonly abteilungen$ = this.store.observe(
    SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN,
    PmsAbteilungStore.getters.all
  );
  readonly abteilungen = toSignal(this.abteilungen$);

  readonly explorerAbteilungen = explore(
    this.abteilungen,
    withFilter(),
    withSort({ defaultSort: { property: 'abteilung.bezeichnung', direction: 'asc' } }),
    withSearch({ defaultSearchTerm: this.textFilterFromQueryParam() })
  );

  readonly loading$ = this.store.observe(
    SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN,
    PmsAbteilungStore.getters.loading
  );

  readonly sort$ = this.store.observe(
    SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN,
    PmsAbteilungStore.getters.sort
  );

  readonly filter$ = this.store.observe(
    SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN,
    PmsAbteilungStore.getters.filter
  );

  readonly filteredItems$: Observable<AbteilungUebersicht[]> = this.abteilungen$.pipe(
    combineLatestWith(this.sort$, this.filter$),
    map(([values, sort, filter]) =>
      this.sortService.sort(this.filterService.filter(values, filter), sort)
    )
  );

  /** The rows are `AbteilungUebersicht`: the counts live under `status`, not on the row. */
  readonly filterConfig: FilterConfig = {
    properties: [
      {
        property: 'abteilung.bezeichnung',
        title: 'Bezeichnung',
        operators: ['like', 'startsWith', 'equal', 'notEqual', 'in', 'notIn'],
      },
      { property: 'abteilung.id', title: 'Abteilungsnummer' },
      { property: 'status.anwesend', title: 'Anwesend' },
      { property: 'status.abwesendgelb', title: 'Abwesend (erwartet)' },
      { property: 'status.abwesendrot', title: 'Abwesend (unerwartet)' },
    ],
  };
  readonly sortConfig: SortConfig = {
    properties: [
      { property: 'abteilung.bezeichnung', title: 'Bezeichnung' },
      { property: 'abteilung.id', title: 'Abteilungsnummer' },
      { property: 'status.anwesend', title: 'Anwesend' },
      { property: 'status.abwesendgelb', title: 'Abwesend (erwartet)' },
      { property: 'status.abwesendrot', title: 'Abwesend (unerwartet)' },
      {
        property: 'status',
        key: 'total',
        title: 'Personen gesamt',
        comparer: (first: unknown, second: unknown) =>
          presenceTotal(first) - presenceTotal(second),
      },
      {
        property: 'status',
        key: 'ratio',
        title: 'Anwesenheitsquote',
        comparer: (first: unknown, second: unknown) =>
          presenceRatio(first) - presenceRatio(second),
      },
    ],
  };

  readonly trackByAbteilungId = (_: number, abteilung: AbteilungUebersicht) =>
    abteilung.id;

  t = translator();
  readonly viewSwitchBarTemplate = viewChild.required('viewSwitchBar', {
    read: TemplateRef,
  });
  readonly searchBarTemplate = viewChild.required('searchBar', {
    read: TemplateRef,
  });
  readonly isMobile = inject(BreakpointService).isMobile;
  readonly loadError = signal<unknown>(null);
  private readonly locale = inject(LOCALE_ID);
  readonly lastLoaded = signal<Date | null>(null);
  readonly lastLoadedLabel = computed(() => {
    const loaded = this.lastLoaded();
    return loaded ? formatDate(loaded, 'HH:mm', this.locale) : null;
  });
  readonly isNarrowed = computed(
    () =>
      !!this.explorerAbteilungen.searchTerm() ||
      this.explorerAbteilungen.filterParams().length > 0
  );
  readonly trailingActionBarTemplate = viewChild.required('trailingActionBar', {
    read: TemplateRef,
  });

  readonly viewOptions: { value: 'list' | 'grid'; label: string; icon: string }[] = [
    { value: 'grid', label: 'Kacheln', icon: 'fa-regular fa-table-cells-large' },
    { value: 'list', label: 'Liste', icon: 'fa-regular fa-list' },
  ];

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly store: Store,
    private readonly filterService: FilterService,
    private readonly sortService: SortService,
    private readonly datePipe: DatePipe,
    private readonly dateNavigatorService: DateNavigatorService,
    private readonly backNavigationService: BackNavigationService,
    private readonly refreshService: RefreshService,
    private readonly abteilungenService: AbteilungenService
  ) {
    interval(AUTO_REFRESH_INTERVAL_MS)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        if (document.visibilityState === 'visible') void this.refresh();
      });
    menuItems(() => [
      {
        name: 'abteilungen',
        type: 'route',
        outlet: 'nav',
        icon: 'fa-regular fa-list',
        title: 'Abteilungen',
        routerLink: ['/statusabfrage'],
      },
    ]);

    menuBar({
      key: 'softapps/pms/statusabfrage/abteilungen',
      title: this.t('Abteilungen'),
      separated: true,
      containerStyle: 'plain',
      template: this.viewSwitchBarTemplate,
      order: 1,
    });
    menuBar({
      key: 'softapps/pms/statusabfrage/abteilungen',
      title: this.t('Abteilungen'),
      separated: true,
      containerStyle: 'plain',
      template: this.searchBarTemplate,
      order: 3,
      visible: computed(() => !this.isMobile()),
    });
    menuBar({
      key: 'softapps/pms/statusabfrage/abteilungen',
      title: this.t('Abteilungen'),
      separated: true,
      containerStyle: 'plain',
      align: 'trailing',
      template: this.trailingActionBarTemplate,
    });

    effect(() => {
      this.viewModeStorage.set({ key: this.viewModeStorageKey }, this.viewMode());
    });
  }

  private readonly viewModeStorage = inject(LocalStorageDataAccess);
  private readonly viewModeStorageKey = 'soft.pms-statusabfrage.abteilungen.viewMode';

  viewMode = signal<'list' | 'grid'>(
    this.viewModeStorage.get<'list' | 'grid'>({ key: this.viewModeStorageKey }) ?? 'list'
  );

  async ngOnInit(): Promise<void> {
    // API Request

    this.store.commit(
      SOFTLINE_FEATURE_TITLE,
      TitleStore.mutations.setSubTitle,
      'Abteilungen'
    );

    this.backNavigationService.set(this);
    this.refreshService.add(this);

    this.unregisterDateChange = this.dateNavigatorService.onDateChange(
      async (date) => await this.onNavigatorChange(date)
    );
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['./../'], { relativeTo: this.activatedRoute });
  }

  ngOnDestroy(): void {
    this.unregisterDateChange?.();

    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setSubTitle, '');

    /*this.store.commit(
      SOFTLINE_FEATURE_COMMANDS,
      CommandStore.mutations.removeSet,
      AbteilungenComponent
    );*/

    this.store.commit(
      SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER,
      PmsPersonalStore.mutations.setFilter,
      null
    );

    this.store.commit(
      SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER,
      PmsPersonalStore.mutations.setSort,
      null
    );

    this.backNavigationService.set(undefined);
    this.refreshService.remove(this);
  }

  async refresh(): Promise<void> {
    await this.load(this.dateNavigatorService.date, false);
  }

  async onNavigatorChange(date: string) {
    await this.load(date, true);
  }

  /** A date change clears the list first; a refresh keeps the cards on screen while it reloads. */
  private async load(date: string, clear: boolean): Promise<void> {
    this.loadError.set(null);
    if (clear) {
      this.store.commit(
        SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN,
        PmsAbteilungStore.mutations.clear
      );
    }
    try {
      const stichdatum = this.datePipe.transform(date, 'yyyy-MM-dd');
      await this.abteilungenService.loadAbteilungen(stichdatum);
      this.lastLoaded.set(new Date());
    } catch (e) {
      this.loadError.set(e);
      showRequestErrors(this.store, e);
    }
  }

  async onFilterChange(filter: Filter | null): Promise<void> {
    this.store.commit(
      SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN,
      PmsAbteilungStore.mutations.setFilter,
      filter
    );
  }

  async onSortChange(sort: Sort<AbteilungUebersicht> | null): Promise<void> {
    this.store.commit(
      SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN,
      PmsAbteilungStore.mutations.setSort,
      sort
    );
  }

  async resetSearch(): Promise<void> {
    this.explorerAbteilungen.search('');
    this.explorerAbteilungen.filter([]);
    await this.router.navigate([], {
      queryParams: { search: null },
      queryParamsHandling: 'merge',
      relativeTo: this.activatedRoute,
    });
  }

  async updateQueryParams(filterText: Event): Promise<void> {
    await this.router.navigate([], {
      queryParams: isDefinedNotEmpty(filterText)
        ? { search: encodeURIComponent((filterText.target as HTMLInputElement).value) }
        : undefined,
      relativeTo: this.activatedRoute,
    });
  }
}
